/**
 * expenseReports.js — the only way the app reads or writes expense reports.
 *
 * Everything goes through /api/expense-reports, never the generic Airtable proxy, because who may
 * see and decide an expense is a server-side question: an expense report says what a colleague
 * spent and on whose job. The endpoint checks the caller on every action, reads included.
 *
 * See docs/EXPENSE_REPORTS.md.
 */

const EXPENSE_ITEMS = [
  'EX- Business Development',
  'EX- Meals and Entertainment',
  'EX- Mileage Exp',
  'EX- Parking Expenses',
  'EX- Permit & Governmental Agency Costs',
  'EX- Reprographics',
  'EX- Shipping (FED EX, UPS, USPS, Messenger)',
  'EX- Travel Food Stipend',
]

/**
 * The curated list the firm actually uses, as it appears in Airtable AND in Core.
 *
 * These strings match Core's own `EX- …` expense item master, which is what makes an approved
 * report recordable without a translation table. Do not "tidy" the names.
 */
export function expenseItemCodes() { return EXPENSE_ITEMS.slice() }

/** Mileage is the one item where Units is the real quantity and Unit Cost is a rate per mile. */
export function isMileage(itemCode) { return String(itemCode || '').includes('Mileage') }

export const EXPENSE_STATUSES = ['Submitted', 'Approved', 'Rejected', 'Recorded in BQE Core']

async function call(payload) {
  const token = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken() : null
  const res = await fetch('/api/expense-reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  })
  const body = await res.json().catch(() => ({}))
  // A failed call is not an empty result. Reading a 401 as "no expenses" is exactly how the
  // posting driver once reported "nothing to post" while 21 payments waited.
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`)
  return body
}

/**
 * @param {'mine'|'review'|'record'|'decided'} scope
 * 'mine' is everyone's own list; 'review' is the approval queue; 'record' is what accounting owes Core;
 * 'decided' is what the caller's reviewees had decided in the last 14 days, with each one's Core status.
 */
export function listExpenseReports(scope = 'mine') { return call({ action: 'list', scope }) }

/** Create or edit one's own report. Editing a rejected one puts it back in the queue. */
export function saveExpenseReport(report) { return call({ action: 'save', report }) }

/** Approve or reject. A rejection must carry a reason — the server refuses one without. */
export function decideExpenseReport(id, decision, memo, phaseId) {
  return call({ action: 'decide', id, decision, memo, phaseId: phaseId || undefined })
}

/** Accounting sends a pay-me-back expense to Core again (after fixing what stopped it). */
export function retryExpenseCore(id, phaseId) { return call({ action: 'retryCore', id, phaseId: phaseId || undefined }) }

/** What the firm owes each person, already in Core and not yet paid back. */
export function listOwed() { return call({ action: 'owed' }) }

/** These went out on the payroll of payrollDate (YYYY-MM-DD): mark them paid in Core. */
export function markExpensesPaid(ids, payrollDate) { return call({ action: 'markPaid', ids, payrollDate }) }

/** Accounting confirms the expense reached Core. */
export function markExpenseRecorded(id, note) { return call({ action: 'markRecorded', id, note }) }

/** Stage a receipt in Storage, then hand the resulting URL to Airtable, which re-hosts it. */
export async function uploadReceipt(reportId, file) {
  const { signedUrl, publicUrl } = await call({ action: 'receiptUploadUrl', filename: file.name })
  const put = await fetch(signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } })
  if (!put.ok) throw new Error('The receipt could not be uploaded.')
  return call({ action: 'attachReceipt', id: reportId, url: publicUrl, filename: file.name })
}
